import PropTypes from 'prop-types';
import { Fragment } from 'react';

import Button from 'shopper/components/Button';

import {
  getArticleImageLink,
  getOfferImageLink,
  toOfferImageAltText,
} from 'lib/image';
import { toArticleLink, toOfferLink, toThreadLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { noop } from 'lib/utils';

import InteractionsListItem from './InteractionsListItem';

const TABS = {
  REVIEWS: 'reviews',
  COMMENTS: 'comments',
  THREADS: 'threads',
  OFFERS: 'offers',
};

const toCommentsLink = (slug, { type, scrollToComment }) => {
  const typeToValidLinkMap = {
    OFFER: toOfferLink(slug, { scrollToComment }),
    ARTICLE: toArticleLink(slug, { scrollToComment }),
    THREAD: toThreadLink(slug, { scrollToComment }),
  };

  return slug ? typeToValidLinkMap[type] : null;
};

const getCommentsImageLink = (url, { type }) => {
  const typeToValidLinkMap = {
    OFFER: getOfferImageLink(url, 100),
    ARTICLE: getArticleImageLink(url, 100),
  };

  return url ? typeToValidLinkMap[type] : null;
};

const InteractionsList = ({
  activeTabId,
  hasNextPage,
  interactions,
  isScrollPaginationEnabled = false,
  onLoadMoreButtonClick = noop,
}) => (
  <div className="flex w-full flex-col items-center">
    {interactions.map((interaction, index) => (
      <Fragment key={index}>
        {activeTabId === TABS.REVIEWS && (
          <InteractionsListItem
            date={interaction.commentDate}
            href={toOfferLink(interaction.offer?.offerSlug, {
              scrollToComment: interaction.commentId,
            })}
            image={getOfferImageLink(interaction.offer?.offerPhoto, 100)}
            imageAlt={toOfferImageAltText(interaction.offer?.offerTitle)}
            text={interaction.commentText}
            title={interaction.offer?.offerTitle}
          />
        )}
        {activeTabId === TABS.COMMENTS && (
          <InteractionsListItem
            date={interaction.commentDate}
            href={toCommentsLink(
              interaction.offer?.offerSlug ||
                interaction.article?.articleSlug ||
                interaction.thread?.threadSlug,
              {
                type:
                  (interaction.offer?.offerSlug && 'OFFER') ||
                  (interaction.article?.articleSlug && 'ARTICLE') ||
                  (interaction.thread?.threadSlug && 'THREAD'),
                scrollToComment: interaction.commentId,
              }
            )}
            image={getCommentsImageLink(
              interaction.offer?.offerPhoto ||
                interaction.article?.articlePhoto,
              {
                type:
                  (interaction.offer?.offerPhoto && 'OFFER') ||
                  (interaction.article?.articlePhoto && 'ARTICLE'),
              }
            )}
            imageAlt={`Imagem de ${
              interaction.offer?.offerTitle || interaction.article?.articleTitle
            }`}
            text={interaction.commentText}
            title={interaction.offer?.offerTitle}
          />
        )}
        {activeTabId === TABS.THREADS && (
          <InteractionsListItem
            blockTitle={interaction.threadTitle}
            date={interaction.threadPublished}
            href={toThreadLink(interaction.threadSlug || interaction.threadId, {
              scrollToComment: interaction.commentId,
            })}
            tags={[interaction.threadCategoryName]}
          />
        )}
        {activeTabId === TABS.OFFERS && (
          <InteractionsListItem
            comments={interaction.offerComments}
            date={interaction.offerPublished}
            href={toOfferLink(interaction.offerSlug)}
            image={getOfferImageLink(interaction.offerPhoto, 100)}
            imageAlt={toOfferImageAltText(interaction.offerTitle)}
            likesCount={interaction.offerLikes}
            tags={interaction.offerTags}
            title={interaction.offerTitle}
            views={interaction.offerClicks}
          />
        )}
      </Fragment>
    ))}
    {hasNextPage && !isScrollPaginationEnabled && (
      <Button
        className="my-4 md:my-6"
        size="size2"
        onClick={onLoadMoreButtonClick}
      >
        {placeholder('actions.seeMore')}
      </Button>
    )}
  </div>
);

InteractionsList.propTypes = {
  activeTabId: PropTypes.string.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  interactions: PropTypes.arrayOf(
    PropTypes.shape({
      commentDate: PropTypes.string,
      commentId: PropTypes.number,
      commentText: PropTypes.string,
      offerComments: PropTypes.number,
      offerClicks: PropTypes.number,
      ratings: PropTypes.shape(),
      offerSlug: PropTypes.string,
      offerPhoto: PropTypes.string,
      offerPublished: PropTypes.string,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
        })
      ),
      offerTitle: PropTypes.string,
      threadCategoryName: PropTypes.string,
      threadSlug: PropTypes.string,
      threadTitle: PropTypes.string,
    })
  ),
  isScrollPaginationEnabled: PropTypes.bool,
  onLoadMoreButtonClick: PropTypes.func,
};

export default InteractionsList;
